import { faArrowRight, faListAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Row,
  Spinner
} from 'react-bootstrap';
import TraplogLegend from './TraplogLegend';
import { useCollapse } from './hooks/useCollapse';
import SummaryMap from './maps/SummaryMap';
import { useStores } from './stores/useStores';

/**
 * This component fetches trapping notifications from the traplog API and displays them.
 *
 * There are no actions to perform on the notifications currently.
 */
function TraplogDashboard(props) {
  const [loading, setLoading] = useState(false);
  const [selectedInsectGroup, setSelectedInsectGroup] = useState();
  const [propertiesWithTrappingZones, setPropertiesWithTrappingZones] =
    useState([]);
  const [propertyStatuses, setPropertyStatuses] = useState([]);
  const { traplogStore } = useStores();
  const [resultsOpen, resultsHandleCollapse] = useCollapse(
    'traplogdashboard-results-open'
  );

  useEffect(() => {
    traplogStore.loadInsectGroups(props.bearerToken, props.refreshBearerToken);
  }, [props.bearerToken]);

  useEffect(() => {
    const insectGroup = traplogStore.insectGroups.find(
      (i) => i.short_name === 'CM'
    );
    setSelectedInsectGroup(insectGroup);

    if (insectGroup) {
      setLoading(true);

      Promise.all([
        fetch(
          `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-statuses/${insectGroup.id}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${props.bearerToken}`,
              SeasonalDatabase: props.selectedDatabase
            }
          }
        )
          .then((res) => {
            if (res.ok) return res.json();
            else if (res.status === 401) props.refreshBearerToken();
            else throw Error(res.statusText);
          })
          .then((data) => {
            if (data) {
              // Sort data by status and by property name
              const thresoldMet = [];
              const nearThreshold = [];
              const noAction = [];
              const noData = [];

              data.forEach((propertyStatus) => {
                let bgColor = 'secondary';

                for (
                  let i = 0;
                  i < propertyStatus.trappingZoneStatuses.length;
                  i++
                ) {
                  const status = propertyStatus.trappingZoneStatuses[i].status;
                  if (
                    [
                      'pre_trap_catch_threshold_met',
                      'accumulation_threshold_met',
                      'average_threshold_met'
                    ].includes(status)
                  ) {
                    bgColor = 'danger';
                    break;
                  } else if (
                    bgColor !== 'danger' &&
                    status === 'near_accumulation_threshold'
                  ) {
                    bgColor = 'warning';
                  } else if (
                    bgColor !== 'danger' &&
                    bgColor !== 'warning' &&
                    status === 'good'
                  ) {
                    bgColor = 'success';
                  }
                }

                const obj = {
                  bgColor: bgColor,
                  ...propertyStatus
                };

                switch (bgColor) {
                  case 'danger':
                    thresoldMet.push(obj);
                    break;
                  case 'warning':
                    nearThreshold.push(obj);
                    break;
                  case 'success':
                    noAction.push(obj);
                    break;
                  case 'secondary':
                    noData.push(obj);
                    break;
                }
              });

              const sortMethod = (a, b) => {
                const nameA = a.propertyName.toUpperCase(); // ignore upper and lowercase
                const nameB = b.propertyName.toUpperCase(); // ignore upper and lowercase
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }

                // names must be equal
                return 0;
              };

              thresoldMet.sort(sortMethod);
              nearThreshold.sort(sortMethod);
              noAction.sort(sortMethod);
              noData.sort(sortMethod);

              setPropertyStatuses([
                ...thresoldMet,
                ...nearThreshold,
                ...noAction,
                ...noData
              ]);
            }
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') console.error(err);
            setPropertyStatuses([]);
          }),
        fetch(
          `${process.env.REACT_APP_TRAPLOG_API_URL}api/properties-trapping-zones`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${props.bearerToken}`,
              SeasonalDatabase: props.selectedDatabase
            }
          }
        )
          .then((res) => {
            if (res.ok) return res.json();
            else if (res.status === 401) props.refreshBearerToken();
            else throw Error(res.statusText);
          })
          .then((data) => {
            setPropertiesWithTrappingZones(data);
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') console.error(err);
            setPropertyStatuses([]);
          })
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [props.bearerToken, props.selectedDatabase, traplogStore.insectGroups]);

  const handleClick = (propertyId, trappingZoneId) => {
    traplogStore.resetTrapping();
    traplogStore.setSelectedInsectGroupId(
      props.bearerToken,
      props.refreshBearerToken,
      props.selectedDatabase,
      selectedInsectGroup.id
    );
    traplogStore.setSelectedTrappingZoneId(trappingZoneId);
    props.goToTrapping(propertyId);
  };

  return loading ? (
    <div className='d-flex justify-content-center pt-5'>
      <Spinner animation='border' variant='primary' />
    </div>
  ) : (
    <Container className='p-0'>
      <Container style={{ height: '400px' }}>
        <SummaryMap
          height={window.innerHeight < 500 ? 250 : window.innerHeight / 2}
          selectedDatabase={props.selectedDatabase}
          bearerToken={props.bearerToken}
          refreshBearerToken={props.refreshBearerToken}
          propertiesWithTrappingZones={propertiesWithTrappingZones}
          propertyStatuses={propertyStatuses}
          onClick={(propertyId, trappingZoneId) =>
            handleClick(propertyId, trappingZoneId)
          }
        />
      </Container>
      <TraplogLegend
        className='m-1 mt-2 text-center'
        propertyStatuses={propertyStatuses}
      />
      <Card className='mt-2'>
        <Card.Header onClick={resultsHandleCollapse} className='text-center'>
          {resultsOpen ? (
            <Fragment>
              {selectedInsectGroup ? `${selectedInsectGroup.name} ` : ''}
              Trapping
              <br />
            </Fragment>
          ) : null}
          <small>
            <FontAwesomeIcon icon={faListAlt} /> {resultsOpen ? 'Hide' : 'Show'}{' '}
            Results
          </small>
        </Card.Header>
        <Collapse in={resultsOpen}>
          <div>
            {propertyStatuses.length > 0
              ? propertyStatuses.map((propertyStatus) => (
                  <Row
                    key={[propertyStatus.propertyId]}
                    className='my-2 mx-1 p-0'
                    noGutters
                  >
                    <Col xs={12} className='bg-light'>
                      <Row noGutters>
                        <Col
                          xs={1}
                          className={`bg-${propertyStatus.bgColor}`}
                        />
                        <Col xs={8} className='py-2 pl-3 pr-0'>
                          <Row>
                            <Col>
                              <b>{propertyStatus.propertyName}</b>
                            </Col>
                          </Row>
                          {Object.keys(propertyStatus.trappingZoneStatuses).map(
                            (trappingZoneId) => (
                              <Row key={trappingZoneId}>
                                <Col>
                                  {
                                    propertyStatus.trappingZoneStatuses[
                                      trappingZoneId
                                    ].trappingZoneName
                                  }
                                  :{' '}
                                  {
                                    propertyStatus.trappingZoneStatuses[
                                      trappingZoneId
                                    ].status_display
                                  }
                                </Col>
                              </Row>
                            )
                          )}
                        </Col>
                        <Col
                          xs={3}
                          className='d-flex align-items-center justify-content-end pr-3'
                        >
                          <Button
                            variant='outline-primary'
                            className='float-right'
                            onClick={() => {
                              handleClick(parseInt(propertyStatus.propertyId));
                            }}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ))
              : null}
          </div>
        </Collapse>
      </Card>
    </Container>
  );
}

TraplogDashboard.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,

  // Links
  goToTrapping: PropTypes.func.isRequired
};

export default observer(TraplogDashboard);
